// src/configuration/HiringRoomTab.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Grid,
    TextField,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    Tooltip,
    IconButton,
    InputAdornment,
    Chip,
} from '@mui/material';
import {
    Save as SaveIcon,
    CheckCircle as CheckCircleIcon,
    Info as InfoIcon,
} from '@mui/icons-material';
import useStore from '../config/store';
import api from '../config/api';
import { useTranslation } from 'react-i18next';

const HiringRoomTab = () => {
    const { authToken, organization, project, showAlert } = useStore.getState();
    const { t } = useTranslation();

    const [config, setConfig] = useState({
        client_id: '',
        client_secret: '',
        email: '',
        password: '',
        connected: false,
        connected_at: null,
    });

    const [loadingConfig, setLoadingConfig] = useState(true);
    const [savingConfig, setSavingConfig] = useState(false);
    const [testingConnection, setTestingConnection] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState(null);

    const loadConfiguration = async () => {
        try {
            setLoadingConfig(true);

            if (!organization || !project) {
                console.warn('Organization o Project no disponibles para cargar configuración');
                setLoadingConfig(false);
                return;
            }

            const response = await api.get(
                '/projects/hiringroom-configuration',
                {
                    params: {
                        organizationId: organization,
                        projectId: project
                    },
                    headers: { Authorization: `Bearer ${authToken}` }
                }
            );

            if (response.data.response.status_response === 'OK') {
                const hiringroomConfig = response.data.response.data;
                setConfig(hiringroomConfig || {
                    client_id: '',
                    client_secret: '',
                    email: '',
                    password: '',
                    connected: false,
                    connected_at: null,
                });

                // Si ya está conectado, establecer el estado de conexión
                if (hiringroomConfig?.connected) {
                    setConnectionStatus('success');
                }

                console.log('Configuración de HiringRoom cargada:', hiringroomConfig);
            } else {
                console.warn('No se pudo cargar la configuración de HiringRoom:', response.data.response.message);
            }
        } catch (err) {
            console.error('Error cargando configuración de HiringRoom:', err);
        } finally {
            setLoadingConfig(false);
        }
    };

    useEffect(() => {
        loadConfiguration();
    }, [organization, project]);

    const handleSave = async () => {
        try {
            setSavingConfig(true);

            if (!organization || !project) {
                showAlert(t('hiringRoomConfig.cannotSave'), 'error');
                return;
            }

            const response = await api.put(
                '/projects/hiringroom-configuration',
                {
                    client_id: config.client_id,
                    client_secret: config.client_secret,
                    email: config.email,
                    password: config.password
                },
                {
                    params: {
                        organizationId: organization,
                        projectId: project
                    },
                    headers: { Authorization: `Bearer ${authToken}` }
                }
            );

            if (response.data.response.status_response === 'OK') {
                showAlert(t('hiringRoomConfig.saveSuccess'), 'success');
                console.log('Configuración de HiringRoom guardada:', response.data.response.data);

                // Actualizar el estado local con la info de conexión
                setConfig(prev => ({
                    ...prev,
                    connected: true,
                    connected_at: new Date().toISOString()
                }));
            } else {
                showAlert(t('hiringRoomConfig.saveError') + ': ' + response.data.response.message, 'error');
            }
        } catch (err) {
            console.error('Error guardando configuración de HiringRoom:', err);
            showAlert(t('hiringRoomConfig.saveErrorGeneral'), 'error');
        } finally {
            setSavingConfig(false);
        }
    };

    const handleTestConnection = async () => {
        try {
            setTestingConnection(true);
            setConnectionStatus(null);

            const response = await api.post(
                '/projects/test-hiringroom-connection',
                {
                    client_id: config.client_id,
                    client_secret: config.client_secret,
                    email: config.email,
                    password: config.password
                },
                {
                    headers: { Authorization: `Bearer ${authToken}` }
                }
            );

            if (response.data.response.status_response === 'OK' && response.data.response.data.success) {
                setConnectionStatus('success');
                showAlert(t('hiringRoomConfig.testSuccess'), 'success');
            } else {
                setConnectionStatus('error');
                showAlert(
                    t('hiringRoomConfig.testError') + ': ' +
                    (response.data.response.data.error || t('hiringRoomConfig.invalidCredentials')),
                    'error'
                );
            }
        } catch (err) {
            console.error('Error probando conexión:', err);
            setConnectionStatus('error');
            showAlert(t('hiringRoomConfig.testErrorGeneral'), 'error');
        } finally {
            setTestingConnection(false);
        }
    };

    const isFormValid = config.client_id && config.client_secret && config.email && config.password;
    const canSave = isFormValid && connectionStatus === 'success';

    if (loadingConfig) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            {t('hiringRoomConfig.title')}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" paragraph sx={{ mb: 0 }}>
                            {t('hiringRoomConfig.subtitle')}
                        </Typography>
                    </Box>

                    {config.connected && (
                        <Chip
                            label={t('hiringRoomConfig.connected')}
                            color="success"
                            size="small"
                            icon={<CheckCircleIcon />}
                        />
                    )}
                </Box>
            </Paper>

            <Card>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label={t('hiringRoomConfig.clientId')}
                                value={config.client_id}
                                onChange={(e) => setConfig(prev => ({ ...prev, client_id: e.target.value }))}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Tooltip title={t('hiringRoomConfig.clientIdTooltip')} arrow placement="top">
                                                <IconButton edge="end" size="small">
                                                    <InfoIcon fontSize="small" color="action" />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label={t('hiringRoomConfig.clientSecret')}
                                type="password"
                                value={config.client_secret}
                                onChange={(e) => setConfig(prev => ({ ...prev, client_secret: e.target.value }))}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Tooltip title={t('hiringRoomConfig.clientSecretTooltip')} arrow placement="top">
                                                <IconButton edge="end" size="small">
                                                    <InfoIcon fontSize="small" color="action" />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label={t('hiringRoomConfig.email')}
                                type="email"
                                value={config.email}
                                onChange={(e) => setConfig(prev => ({ ...prev, email: e.target.value }))}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Tooltip title={t('hiringRoomConfig.emailTooltip')} arrow placement="top">
                                                <IconButton edge="end" size="small">
                                                    <InfoIcon fontSize="small" color="action" />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label={t('hiringRoomConfig.password')}
                                type="password"
                                value={config.password}
                                onChange={(e) => setConfig(prev => ({ ...prev, password: e.target.value }))}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Tooltip title={t('hiringRoomConfig.passwordTooltip')} arrow placement="top">
                                                <IconButton edge="end" size="small">
                                                    <InfoIcon fontSize="small" color="action" />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>

                    <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSave}
                            disabled={savingConfig || !canSave}
                            startIcon={savingConfig ? <CircularProgress size={20} /> : <SaveIcon />}
                        >
                            {savingConfig ? t('hiringRoomConfig.saving') : t('hiringRoomConfig.saveConfig')}
                        </Button>

                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleTestConnection}
                            disabled={testingConnection || !isFormValid}
                            startIcon={testingConnection ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                        >
                            {testingConnection ? t('hiringRoomConfig.testing') : t('hiringRoomConfig.testConnection')}
                        </Button>
                    </Box>

                    {/* Mensaje informativo si no ha probado la conexión */}
                    {!connectionStatus && isFormValid && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            {t('hiringRoomConfig.testBeforeSave')}
                        </Alert>
                    )}

                    {/* Estado de la conexión */}
                    {connectionStatus === 'success' && (
                        <Alert severity="success" sx={{ mt: 2 }}>
                            {t('hiringRoomConfig.connectionSuccess')}
                        </Alert>
                    )}

                    {connectionStatus === 'error' && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {t('hiringRoomConfig.connectionError')}
                        </Alert>
                    )}
                </CardContent>
            </Card>
        </>
    );
};

export default HiringRoomTab;